import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Plus, Search, Edit, Trash2, Loader2, Star, FolderTree, ChevronRight, Home } from 'lucide-react'
import { apiGet } from '../lib/api'

interface CategoriaNode {
  id: string
  nombre: string
  slug: string
  padreId: string
  imagen: string
  orden: number
  destacada: boolean
  muestraHome: boolean
  productCount: number
  paqueteria: string
  nivel: number
  hijos: CategoriaNode[]
}

interface CategoriasTreeData {
  total: number
  data: CategoriaNode[]
  tree: CategoriaNode[]
}

export function Categorias() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, isFetching } = useQuery<CategoriasTreeData>({
    queryKey: ['categorias-tree', searchTerm],
    queryFn: () =>
      apiGet(`/api/admin/categorias?tree=1${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''}`),
    placeholderData: (prev) => prev,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/admin/categorias/${id}`, { method: 'DELETE' })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Error' }))
        throw new Error(err.error || 'Error eliminando')
      }
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias-tree'] })
    },
    onError: (e: Error) => {
      alert(e.message)
    },
  })

  // Asigna la paquetería de una categoría (envío por Estafeta / FedEx).
  const carrierMutation = useMutation({
    mutationFn: async (payload: { id: string; carrier: string }) => {
      const r = await fetch(`/api/admin/categorias/${payload.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipping_carrier: payload.carrier || null }),
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Error' }))
        throw new Error(err.error || 'Error guardando paquetería')
      }
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias-tree'] })
    },
    onError: (e: Error) => alert(e.message),
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setSearchTerm(q.trim())
  }

  function onDelete(id: string, nombre: string) {
    if (confirm(`¿Eliminar la categoría "${nombre}"?`)) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <>
      <Topbar
        title="Categorías"
        subtitle={data ? `${data.total.toLocaleString('es-MX')} categorías en el árbol` : 'Cargando…'}
        actions={
          <button
            onClick={() => navigate({ to: '/categorias/nueva' as any })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nueva
          </button>
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre o slug…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando categorías…</p>
            </div>
          )}

          {data && (
            <div className="relative overflow-x-auto">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">Categoría</th>
                    <th className="px-4 py-3 font-medium">Slug</th>
                    <th className="px-4 py-3 font-medium text-right">Productos</th>
                    <th className="px-4 py-3 font-medium">Paquetería</th>
                    <th className="px-4 py-3 font-medium">Flags</th>
                    <th className="w-20 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-ink-500">
                        No hay categorías para mostrar
                      </td>
                    </tr>
                  )}
                  {data.data.map((c) => (
                    <tr
                      key={c.id}
                      className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span style={{ paddingLeft: `${c.nivel * 20}px` }} className="inline-flex items-center gap-2">
                            {c.nivel > 0 ? (
                              <ChevronRight className="size-3.5 text-ink-400" />
                            ) : (
                              <FolderTree className="size-4 text-brand-600" />
                            )}
                            <span className="font-medium text-ink-900">{c.nombre}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-600">{c.slug}</td>
                      <td className="px-4 py-3 text-right">
                        {c.productCount > 0 ? (
                          <span className="font-semibold text-ink-900">
                            {c.productCount.toLocaleString('es-MX')}
                          </span>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={c.paqueteria || ''}
                          onChange={(e) =>
                            carrierMutation.mutate({ id: c.id, carrier: e.target.value })
                          }
                          className="rounded-lg border border-ink-200 bg-white px-2 py-1 text-xs outline-none focus:border-brand-500"
                          title="Paquetería de envío para esta categoría"
                        >
                          <option value="">— Sin asignar —</option>
                          <option value="estafeta">Estafeta ($160)</option>
                          <option value="fedex">FedEx ($260)</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.destacada && (
                            <Badge variant="warning">
                              <Star className="size-3" /> Destacada
                            </Badge>
                          )}
                          {c.muestraHome && (
                            <Badge variant="info">
                              <Home className="size-3" /> Home
                            </Badge>
                          )}
                          {!c.destacada && !c.muestraHome && (
                            <span className="text-xs text-ink-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button
                            onClick={() => navigate({ to: `/categorias/${c.id}` as any })}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            title="Editar"
                          >
                            <Edit className="size-4" />
                          </button>
                          <button
                            onClick={() => onDelete(c.id, c.nombre)}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700"
                            title="Eliminar"
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
